/**
 * The species' highest Mega Level right now (3, or 4 once Super Max is
 * released): the curated list plus the admin schedule plus the live event
 * feed. The feed is fetched once per session into a module cache (no
 * react-query, so the hook works wherever the Mega block renders, providers
 * or not) and every mounted block re-reads when it lands.
 */
import { useEffect, useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { megaMaxLevel, superMaxSpeciesFromFeed, type SuperMaxFeedEvent } from "@/lib/superMax";

let feedSpecies: Set<number> | null = null;
let feedPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

function loadFeed(): Promise<void> {
  if (feedPromise) return feedPromise;
  feedPromise = (async () => {
    try {
      const res = await apiRequest("GET", "/api/events/feed");
      const body = (await res.json()) as { feed?: Array<{ event?: SuperMaxFeedEvent }> };
      const events = (body.feed ?? []).flatMap((row) => (row.event ? [row.event] : []));
      feedSpecies = superMaxSpeciesFromFeed(events);
    } catch {
      feedSpecies = new Set();
    }
    for (const l of listeners) l();
  })();
  return feedPromise;
}

/** Test seam: forget the fetched feed. */
export function __resetSuperMaxFeedForTests(): void {
  feedSpecies = null;
  feedPromise = null;
}

export function useMegaMaxLevel(dexId: number): 3 | 4 {
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    if (feedSpecies == null) void loadFeed();
    return () => {
      listeners.delete(l);
    };
  }, []);
  return megaMaxLevel(dexId, feedSpecies ?? undefined);
}
